import {
  getBusinessDataForUserApi,
} from 'SupabseHandler';
import {
  UserRoleAtom,
  userDetails as userDetailsAtom,
  isAutheticated as isAutheticatedAtom,
  businessData as businessDataAtom,
} from 'atoms';
import ButtonLarge from 'common/ButtonLarge';
import Heading from 'common/Heading';
import PasswordInput from 'common/PasswordInput';
import routes from 'config/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import supabaseHandler from 'utils/SupaBase';
import { toasterNotification } from 'utils/toaster';

import React, { useEffect, useState } from 'react';

const getSupabase = () => {
   
    if (!supabaseHandler) {
        console.error('Supabase supabaseHandler not initialized - check environment variables');
        return null;
    }
    return supabaseHandler;
};

const LoginPage = () => {
  const [values, setValues] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [user, setUser] = useRecoilState(userDetailsAtom);
  const [userRole, setUserRole] = useRecoilState(UserRoleAtom);
  const [isAuthenticated, setIsAuthenticated] = useRecoilState(isAutheticatedAtom);
  const [business, setBusiness] = useRecoilState(businessDataAtom);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setIsFormValid(values.email.trim() !== '' && values.password.trim() !== '');
  }, [values]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const getUserProfile = async (userId) => {
    const supabase = getSupabase();
    if (!supabase) {
      return null;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .eq('app_id', 'provider')
      .single();

    if (error || !data) {
      console.error('App-specific user not found:', error);
      return null;
    }

    return data;
  };

  const fetchBusiness = async (userId, accessToken) => {
    const supabase = getSupabase();
    if (!supabase) {
      throw new Error('Authentication service not available');
    }
    
    if (!accessToken) {
      throw new Error('No authentication token found');
    }
    
    const businessByUid = await getBusinessDataForUserApi(userId, accessToken);
    // API now returns a normalized array (sorted: owned by user first, then by id desc)
    const list = Array.isArray(businessByUid) ? businessByUid : (businessByUid ? [businessByUid] : []);

    if (list.length > 0) {
      setBusiness(list[0]);
      return;
    }

    // If not found, try office_staff or provider tables
    const { data: officeStaff } = await supabase
      .from('office_staff')
      .select('*')
      .eq('user_id', userId);

    const { data: provider } = await supabase
      .from('provider')
      .select('*')
      .eq('user_id', userId);

    const userBusinessId =
      officeStaff?.[0]?.business_id || provider?.[0]?.business_id;
    if(userBusinessId){
      const { data: businessData } = await supabase
      .from('business')
      .select('*')
      .eq('id', userBusinessId)
      .single();
      setBusiness(businessData);
    }
    

    if (!userBusinessId) {
      throw new Error('Business not found for user');
    }

    // Set role
    const roleId = officeStaff?.[0]?.role_id;
    if (roleId === 1) {
      setUserRole('Admin');
      localStorage.setItem('userRole', 'Admin');
    } else if (roleId === 2) {
      setUserRole('General');
      localStorage.setItem('userRole', 'General');
    } else {
      setUserRole('');
      localStorage.setItem('userRole', '');
    }

    
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const supabase = getSupabase();
      if (!supabase) {
        throw new Error('Authentication service not available');
      }

      // Sign in with backend API
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/auth/v1/token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            grant_type: 'password',
            email: values.email,
            password: values.password,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || 'Invalid credentials';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      const backendData = result.data || result;

      if (!backendData?.user) {
        throw new Error('Invalid credentials');
      }

      // Transform backend response to match Supabase format
      const authData = {
        user: backendData.user,
        session: {
          access_token: backendData.access_token,
          refresh_token: backendData.refresh_token,
          expires_at: backendData.expires_at,
          expires_in: backendData.expires_in,
          token_type: backendData.token_type,
          user: backendData.user,
        },
      };

      // Set Supabase session so AuthContext can access the token
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: backendData.access_token,
        refresh_token: backendData.refresh_token,
      });

      if (sessionError) {
        console.error('Error setting Supabase session:', sessionError);
        // Continue anyway as we have the data
      }

      const userProfile = await getUserProfile(authData.user.id);
      if (!userProfile) {
        navigate('/access-denied');
        return;
      }

      // Ensure user_metadata has name for avatar/display (from profile if backend didn't return it)
      const meta = authData.user.user_metadata || {};
      const hasNameInMeta = !!(meta.first_name || meta.last_name || meta.full_name);
      if (!hasNameInMeta && userProfile) {
        const profileFirst = userProfile.first_name || '';
        const profileLast = userProfile.last_name || '';
        const profileFull = userProfile.full_name || '';
        if (profileFirst || profileLast || profileFull) {
          authData.user.user_metadata = {
            ...meta,
            ...(profileFirst && { first_name: profileFirst }),
            ...(profileLast && { last_name: profileLast }),
            ...(profileFull && !(profileFirst || profileLast) && { full_name: profileFull }),
          };
        }
      }

      // Save user data
      setUser(authData);
      localStorage.setItem('user', JSON.stringify(authData));

      await fetchBusiness(authData.user.id, backendData.access_token);

      setIsAuthenticated(true);
      toasterNotification('success', '', 'Logged In');

      const from = location.state?.from;
      const targetPath = from
        ? (from.pathname || '') + (from.search || '') || '/business'
        : '/business';
      navigate(targetPath || '/business');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-10 items-center w-full">
      <div className="w-full flex flex-col gap-7 items-center">
        <div className="flex flex-col gap-1 items-center">
          <Heading>Welcome back!</Heading>
          <span className="text-base text-center font-poppins">Sign in to your provider account.</span>
        </div>

        <div className="w-full flex flex-col gap-4">
          <input
            className={`common-input p-2.5 px-5 focus:!bg-white ${error ? 'error-field' : ''}`}
            autoFocus
            type="email"
            placeholder="Enter your email"
            value={values.email}
            name="email"
            onChange={handleChange}
          />

          <PasswordInput
            placeholder="Enter your password"
            value={values.password}
            onChange={handleChange}
            name="password"
            isError={!!error}
          />

          <p
            className="text-right text-primary text-sm font-poppins font-normal cursor-pointer hover:underline"
            onClick={() => navigate(routes.auth.forgotPassword)}
          >
            Forgot Password?
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 w-full">
        <ButtonLarge
          onClick={handleLogin}
          disabled={!isFormValid}
          loading={loading}
          className="w-full font-bold"
        >
          Sign In
        </ButtonLarge>
        <p className="help-text text-center">
          Don’t have an account?{' '}
          <span
            className="text-primary font-semibold cursor-pointer hover:underline"
            onClick={() => navigate(routes.auth.register)}
          >
            Sign Up
          </span>
        </p>
        {error && <p className="text-red-500 text-center">{error}</p>}
      </div>
    </div>
  );
};

export default LoginPage;